import React, { memo, useCallback, useEffect, useState, Fragment } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { DCard, BtnGold, BtnGhost } from '../components/MarketplaceAtoms.js';
import { listingThumbUrlFromRow } from '../../lib/listingsService.js';
import {
  fetchPartnerStoreCustody,
  fetchListingRowForCustodyPreview,
  insertPartnerStoreCustody,
  updatePartnerStoreCustodyRow,
} from '../../lib/partnerCustodyService.js';
import {
  PARTNER_CUSTODY_STATUS_LABEL_ES,
  partnerCustodyNextStatus,
} from '../constants/partnerCustody.js';
import { isAuctionPendingVerification, auctionStatusLabel } from '../constants/auctionStatus.js';
import SectionPartnerLogistics from '../components/SectionPartnerLogistics.js';
import PartnerSlaBanner from '../components/PartnerSlaBanner.js';
import TransactionShipmentPanel from '../components/TransactionShipmentPanel.js';
import { mergeTxLogisticsForPanel } from '../constants/transactionLogistics.js';

function listingFromCustodyRow(row) {
  const L = row?.listing ?? row?.listings;
  const listing = Array.isArray(L) ? L[0] : L;
  return listing || null;
}

function sellerUsernameFromListing(listing) {
  const prof = listing?.profiles;
  const p = Array.isArray(prof) ? prof[0] : prof;
  return p?.username ?? null;
}

const SectionCustodiaPartner = memo(function SectionCustodiaPartner({
  partner,
  storeRoleRow,
  onError,
  onNotifySuccess,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newListingId, setNewListingId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState('');
  const [advanceModal, setAdvanceModal] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [buyerDraft, setBuyerDraft] = useState('');
  const [pinDraft, setPinDraft] = useState('');
  const [shipmentPanelRowId, setShipmentPanelRowId] = useState(null);

  const load = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    onError('');
    const { data, error } = await fetchPartnerStoreCustody(partner.id);
    if (error) {
      onError(error);
      setRows([]);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, [partner?.id, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const openPreview = async () => {
    setPreviewErr('');
    setPreview(null);
    const id = newListingId.trim();
    if (!/^[0-9a-f-]{36}$/i.test(id)) {
      setPreviewErr('Introduce un UUID de anuncio válido.');
      return;
    }
    const { row, error } = await fetchListingRowForCustodyPreview(id);
    if (error) {
      setPreviewErr(error);
      return;
    }
    if (!row?.id) {
      setPreviewErr('No se encontró ese anuncio o no tienes permiso para verlo.');
      return;
    }
    setPreview(row);
  };

  const submitNew = async () => {
    if (!partner?.id) return;
    const id = newListingId.trim();
    if (!preview?.id || id !== preview.id) {
      onError('Carga y revisa el anuncio antes de registrar.');
      return;
    }
    onError('');
    const { error } = await insertPartnerStoreCustody({ partnerId: partner.id, listingId: preview.id });
    if (error) {
      onError(error);
      return;
    }
    onNotifySuccess?.('Operación de custodia registrada (estado: Esperando).');
    setModalOpen(false);
    setNewListingId('');
    setPreview(null);
    await load();
  };

  const openAdvance = row => {
    const next = partnerCustodyNextStatus(row.status);
    if (!next) return;
    setAdvanceModal({ row, next });
    setNotesDraft(row.partner_notes ?? '');
    setBuyerDraft(row.buyer_profile_id ?? '');
    setPinDraft(row.delivery_pin ?? '');
  };

  const confirmAdvance = async () => {
    if (!partner?.id || !advanceModal?.row?.id || !advanceModal?.next) return;
    const row = advanceModal.row;
    const next = advanceModal.next;
    setBusyId(row.id);
    onError('');
    const opts = {
      id: row.id,
      partnerId: partner.id,
      nextStatus: next,
      partnerNotes: notesDraft,
    };
    if (next === 'listo') {
      opts.buyerProfileId = buyerDraft;
      opts.deliveryPin = pinDraft;
    }
    const { error } = await updatePartnerStoreCustodyRow(opts);
    setBusyId(null);
    if (error) {
      onError(error);
      return;
    }
    onNotifySuccess?.(`Estado actualizado: ${PARTNER_CUSTODY_STATUS_LABEL_ES[next] || next}`);
    setAdvanceModal(null);
    await load();
  };

  return (
    <div>
      <SectionPartnerLogistics
        role="store"
        roleRow={storeRoleRow}
        onError={onError}
        onNotifySuccess={onNotifySuccess}
      />
      <PartnerSlaBanner role="store" />
      <DCard style={{ padding: 18, marginBottom: 16, background: 'rgba(200,168,75,0.06)', borderColor: 'rgba(200,168,75,0.22)' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 16, color: C.gold }}>Custodia y verificación de discos</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
          Pipeline: <strong style={{ color: '#fff' }}>Esperando → Recibido → En revisión → Verificado → Listo → Entregado</strong>. Solo puedes avanzar
          un estado cada vez. En <strong style={{ color: '#fff' }}>subastas pendientes</strong>, al marcar «Verificado» la subasta se publica sola. En ventas
          con verificador, el alta manual exige anuncio activo con envío «Partner verificador» o inventario de tu tienda.
        </p>
        <div style={{ marginTop: 14 }}>
          <BtnGold type="button" style={{ fontSize: 12 }} onClick={() => setModalOpen(true)}>
            + Nueva operación
          </BtnGold>
          <BtnGhost type="button" style={{ fontSize: 12, marginLeft: 8 }} onClick={() => load()} disabled={loading}>
            {loading ? 'Cargando…' : 'Recargar tabla'}
          </BtnGhost>
        </div>
      </DCard>

      {modalOpen ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(255,255,255,0.12)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Registrar custodia</div>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 12, lineHeight: 1.5 }}>
            Pega el UUID del anuncio (`listings.id`). Comprueba la vista previa y confirma. No puede haber otra operación abierta para el mismo anuncio y
            partner.
          </p>
          <input
            value={newListingId}
            onChange={e => setNewListingId(e.target.value)}
            placeholder="UUID del anuncio"
            style={{
              width: '100%',
              maxWidth: 420,
              padding: '10px 12px',
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.15)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 13,
              marginBottom: 10,
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <BtnGhost type="button" style={{ fontSize: 12 }} onClick={() => void openPreview()}>
              Cargar anuncio
            </BtnGhost>
          </div>
          {previewErr ? <div style={{ fontSize: 12, color: '#ffb4b4', marginBottom: 10 }}>{previewErr}</div> : null}
          {preview ? (
            <div style={{ padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.04)', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {preview.artist} — {preview.title}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>
                Estado anuncio: {auctionStatusLabel(preview.status) || preview.status || '—'}
                {preview.is_auction ? ' · Subasta' : ''}
                {preview.ships_verified ? ' · Verificador' : ''}
              </div>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnGold type="button" style={{ fontSize: 12 }} onClick={() => void submitNew()} disabled={!preview}>
              Registrar en «Esperando»
            </BtnGold>
            <BtnGhost
              type="button"
              style={{ fontSize: 12 }}
              onClick={() => {
                setModalOpen(false);
                setPreview(null);
                setPreviewErr('');
              }}
            >
              Cancelar
            </BtnGhost>
          </div>
        </DCard>
      ) : null}

      {advanceModal ? (
        <DCard style={{ padding: 18, marginBottom: 16, borderColor: 'rgba(200,168,75,0.25)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
            Avanzar a «{PARTNER_CUSTODY_STATUS_LABEL_ES[advanceModal.next] || advanceModal.next}»
          </div>
          <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', display: 'block', marginBottom: 6 }}>Notas internas (opcional)</label>
          <textarea
            value={notesDraft}
            onChange={e => setNotesDraft(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              maxWidth: 480,
              padding: 10,
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 12,
              marginBottom: 12,
              boxSizing: 'border-box',
            }}
          />
          {advanceModal.next === 'listo' ? (
            <>
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', display: 'block', marginBottom: 6 }}>
                UUID comprador (opcional, perfil que recoge)
              </label>
              <input
                value={buyerDraft}
                onChange={e => setBuyerDraft(e.target.value)}
                placeholder="UUID perfil comprador"
                style={{
                  width: '100%',
                  maxWidth: 420,
                  padding: '9px 11px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontSize: 12,
                  marginBottom: 10,
                  boxSizing: 'border-box',
                }}
              />
              <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', display: 'block', marginBottom: 6 }}>
                PIN / código entrega (opcional)
              </label>
              <input
                value={pinDraft}
                onChange={e => setPinDraft(e.target.value)}
                placeholder="Ej: 4821"
                style={{
                  width: 200,
                  padding: '9px 11px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontSize: 12,
                  marginBottom: 12,
                  boxSizing: 'border-box',
                }}
              />
            </>
          ) : null}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <BtnGold type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => void confirmAdvance()}>
              Confirmar cambio
            </BtnGold>
            <BtnGhost type="button" style={{ fontSize: 12 }} disabled={!!busyId} onClick={() => setAdvanceModal(null)}>
              Cancelar
            </BtnGhost>
          </div>
        </DCard>
      ) : null}

      <DCard style={{ padding: 0, overflow: 'hidden', borderColor: 'rgba(255,255,255,0.1)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 720 }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.35)', color: 'rgba(255,255,255,0.55)', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Disco</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Vendedor</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Estado</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Notas</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Actualizado</th>
                <th style={{ padding: '10px 12px', fontWeight: 700 }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ padding: 22, color: 'rgba(255,255,255,0.45)' }}>
                    Cargando operaciones…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 22, color: 'rgba(255,255,255,0.45)' }}>
                    Aún no hay operaciones. Registra la primera con «Nueva operación».
                  </td>
                </tr>
              ) : (
                rows.map(row => {
                  const listing = listingFromCustodyRow(row);
                  const thumb = listing ? listingThumbUrlFromRow(listing) : null;
                  const sellerUser = sellerUsernameFromListing(listing);
                  const next = partnerCustodyNextStatus(row.status);
                  return (
                    <Fragment key={row.id}>
                    <tr style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              flexShrink: 0,
                              background: thumb ? `url(${thumb}) center/cover` : 'rgba(255,255,255,0.06)',
                            }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: '#fff', fontWeight: 600, lineHeight: 1.35 }}>
                              {listing ? `${listing.artist} — ${listing.title}` : '—'}
                            </div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                              {listing?.is_auction && isAuctionPendingVerification(listing?.status) ? (
                                <span style={{ color: C.gold, fontWeight: 600 }}>Subasta pendiente · </span>
                              ) : null}
                              {listing?.id || row.listing_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.75)' }}>
                        {sellerUser ? `@${sellerUser}` : listing?.seller_id ? String(listing.seller_id).slice(0, 8) + '…' : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: 999,
                            background: 'rgba(200,168,75,0.15)',
                            border: `0.5px solid ${C.gold}`,
                            color: C.gold,
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          {PARTNER_CUSTODY_STATUS_LABEL_ES[row.status] || row.status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.55)', maxWidth: 200 }}>
                        {row.partner_notes ? String(row.partner_notes).slice(0, 160) : '—'}
                        {row.delivery_pin ? (
                          <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(200,168,75,0.85)' }}>PIN: {row.delivery_pin}</div>
                        ) : null}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                        {row.updated_at ? new Date(row.updated_at).toLocaleString('es-ES') : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                        {next ? (
                          <BtnGold
                            type="button"
                            style={{ fontSize: 11, padding: '6px 12px' }}
                            disabled={busyId === row.id}
                            onClick={() => openAdvance(row)}
                          >
                            → {PARTNER_CUSTODY_STATUS_LABEL_ES[next]}
                          </BtnGold>
                        ) : (
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Cerrada</span>
                        )}
                        {row.transaction_id ? (
                          <BtnGhost
                            type="button"
                            style={{ fontSize: 11, padding: '6px 12px', marginTop: 6, display: 'block' }}
                            onClick={() => setShipmentPanelRowId(id => (id === row.id ? null : row.id))}
                          >
                            {shipmentPanelRowId === row.id ? 'Ocultar envío' : 'Envío / mensajes'}
                          </BtnGhost>
                        ) : null}
                      </td>
                    </tr>
                    {shipmentPanelRowId === row.id && row.transaction_id ? (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 12px 14px', background: 'rgba(0,0,0,0.2)' }}>
                          <TransactionShipmentPanel
                            transaction={mergeTxLogisticsForPanel(row)}
                            viewerRole="partner"
                            currentUserId={partner?.profile_id}
                            onUpdated={() => void load()}
                            title="Logística y mensajes de la venta"
                          />
                        </td>
                      </tr>
                    ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </DCard>
    </div>
  );
});

export default SectionCustodiaPartner;
